import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { 
    Notification, 
    NotificationsResponse, 
    UserNotification 
} from 'app/layout/common/notifications/notifications.types';
import { AuthService } from 'app/core/auth/auth.service';
import {
    BehaviorSubject,
    map,
    Observable,
    Subject,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'app/../environments/environment';

export interface NotificationQueryParams {
    page?: number;
    limit?: number;
    status?: string;
    channel?: string;
    type?: string;
    category?: string;
    priority?: string;
    dateRange?: string;
    startDate?: string;
    endDate?: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _baseUrl = environment.apiBaseUrl;
    private _apiNotifications: BehaviorSubject<UserNotification[]> = new BehaviorSubject<UserNotification[]>([]);
    private _filters: BehaviorSubject<any> = new BehaviorSubject(null);
    private _pagination: BehaviorSubject<any> = new BehaviorSubject(null);
    private _notifications: BehaviorSubject<Notification[]> =
        new BehaviorSubject<Notification[]>([]);
    private _liveNotifications$ = new Subject<Notification>();
    private socket?: Socket;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private authService: AuthService
    ) {
        this._initializeSocket();
    }

    private _initializeSocket(): void {
        const user = this.authService.getUser();
        if (!user?._id || this.socket) {
            return;
        }

        this.socket = io(this._baseUrl.replace('/v1', ''), {
            transports: ['websocket', 'polling'],
            withCredentials: true,
            query: {
                userId: user._id,
            },
        });

        this.socket.on('connect', () => {
            console.log('Socket connected', this.socket?.id);
        });

        this.socket.emit('joinChat', { chatId: '', userId: user._id });

        this.socket.on('notification:new', (data: any) => {
            this.getNotifications().subscribe();
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for API notifications
     */
    get apiNotifications$(): Observable<UserNotification[]> {
        return this._apiNotifications.asObservable();
    }

    /**
     * Getter for filters
     */
    get filters$(): Observable<any> {
        return this._filters.asObservable();
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<any> {
        return this._pagination.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Socket Methods
    // -----------------------------------------------------------------------------------------------------

    onNotification(): Observable<Notification> {
        return this._liveNotifications$.asObservable();
    }

    // Emit events if needed
    sendMessage(event: string, payload: any) {
        this.socket?.emit(event, payload);
    }

    disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ API Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications from API with filters
     */
    getNotifications(params?: NotificationQueryParams): Observable<NotificationsResponse> {
        let httpParams = new HttpParams();
        
        if (params) {
            Object.keys(params).forEach(key => {
                if (params[key] !== undefined && params[key] !== null) {
                    httpParams = httpParams.set(key, params[key].toString());
                }
            });
        }

        return this._httpClient.get<NotificationsResponse>(`${this._baseUrl}/notification`, { params: httpParams })
            .pipe(
                tap((response) => {
                    if (response.success) {
                        this._apiNotifications.next(response.data.notifications);
                        this._filters.next(response.data.filters);
                        this._pagination.next(response.data.pagination);
                    }
                })
            );
    }

    /**
     * Mark a single notification as read
     */
    markAsRead(notificationId: string): Observable<any> {
        return this._httpClient.get(`${this._baseUrl}/notification/mark-read/${notificationId}`)
            .pipe(
                tap(() => {
                    const notifications = this._apiNotifications.value;
                    const updatedNotifications = notifications.map(notification => 
                        notification._id === notificationId 
                            ? { ...notification, status: 'read' as const }
                            : notification
                    );
                    this._apiNotifications.next(updatedNotifications);
                    this.updateUnreadCount();
                })
            );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<any> {
        return this._httpClient.get(`${this._baseUrl}/notification/mark-all-read`)
            .pipe(
                tap(() => {
                    // Update local state
                    const notifications = this._apiNotifications.value;
                    const updatedNotifications = notifications.map(notification => ({
                        ...notification,
                        status: 'read' as const
                    }));
                    this._apiNotifications.next(updatedNotifications);
                    this.updateUnreadCount();
                })
            );
    }

    /**
     * Convert API notification to legacy format for compatibility
     */
    private convertToLegacyFormat(apiNotification: UserNotification): Notification {
        return {
            id: apiNotification._id,
            icon: this.getNotificationIcon(apiNotification.notification.type),
            title: apiNotification.notification.title,
            description: apiNotification.notification.message,
            time: apiNotification.createdAt,
            link: apiNotification.notification.link,
            useRouter: true,
            read: apiNotification.status === 'read'
        };
    }

    /**
     * Get appropriate icon based on notification type
     */
    private getNotificationIcon(type: string): string {
        const iconMap: { [key: string]: string } = {
            booking: 'heroicons_mini:calendar-days',
            payment: 'heroicons_mini:credit-card',
            account: 'heroicons_mini:user-circle',
            system: 'heroicons_mini:cog-6-tooth',
            promotion: 'heroicons_mini:megaphone'
        };
        
        return iconMap[type] || 'heroicons_mini:bell';
    }

    /**
     * Update unread count (for legacy compatibility)
     */
    private updateUnreadCount(): void {
        const apiNotifications = this._apiNotifications.value;
        const legacyNotifications = apiNotifications.map(notification => 
            this.convertToLegacyFormat(notification)
        );
        this._notifications.next(legacyNotifications);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Legacy Methods (for backward compatibility)
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications (legacy)
     */
    getAll(): Observable<Notification[]> {
    return this.getNotifications().pipe(
        map(response => {
            const notifications = response?.data?.notifications ?? [];

            const legacyNotifications = notifications.map(notification =>
                this.convertToLegacyFormat(notification)
            );

            this._notifications.next(legacyNotifications);
            return legacyNotifications;
        })
    );
}

}
